import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Button, Popover, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import {
  copyTextToClipboard,
  extractShortenedLinkFromApiResponse,
} from "helpers/GeneralHelper";
import { shortenCommonLink } from "model/MetaModel";

const MetadataLinkTooltipBox = ({
  isOpen,
  toggle,
  targetRef,
  url = "",
  title = "Link",
}) => {
  const [shortening, setShortening] = useState(false);
  const normalizedUrl = String(url ?? "").trim();

  useEffect(() => {
    if (!isOpen) {
      setShortening(false);
    }
  }, [isOpen]);

  const handleCopy = async (event) => {
    event?.stopPropagation?.();
    if (!normalizedUrl) {
      toast.error("Link is not available");
      return;
    }
    await copyTextToClipboard(normalizedUrl, "Unable to copy link");
  };

  const handleShortLink = async (event) => {
    event?.stopPropagation?.();
    if (!normalizedUrl) {
      toast.error("Link is not available");
      return;
    }

    setShortening(true);
    try {
      const response = await shortenCommonLink(normalizedUrl);
      const shortLink = extractShortenedLinkFromApiResponse(response);
      if (!shortLink) {
        toast.error(response?.message || "Unable to shorten link");
        return;
      }
      await copyTextToClipboard(shortLink, "Unable to copy short link");
    } finally {
      setShortening(false);
    }
  };

  const handleOpenInNewTab = (event) => {
    event?.stopPropagation?.();
    if (!normalizedUrl) return;
    window.open(normalizedUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Popover
      isOpen={isOpen}
      target={targetRef}
      toggle={toggle}
      placement="top"
      trigger="legacy"
      fade={false}
      className="metadata-link-tooltip-popover"
      popperClassName="metadata-link-tooltip-popover__popper"
      innerClassName="metadata-link-tooltip-popover__inner"
      hideArrow={false}
    >
      <div className="metadata-link-tooltip-box" onClick={(event) => event.stopPropagation()}>
        <div className="metadata-link-tooltip-box__header">
          <div className="metadata-link-tooltip-box__title-wrap">
            <span className="metadata-link-tooltip-box__icon" aria-hidden="true">
              <i className="mdi mdi-link-variant" />
            </span>
            <span className="metadata-link-tooltip-box__title">{title}</span>
          </div>
          <button
            type="button"
            className="metadata-link-tooltip-box__close"
            onClick={toggle}
            aria-label="Close"
          >
            <i className="mdi mdi-close" />
          </button>
        </div>

        <div className="metadata-link-tooltip-box__body">
          {normalizedUrl ? (
            <>
              <div className="metadata-link-tooltip-box__url-panel">
                <a
                  href={normalizedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="metadata-link-tooltip-box__url"
                  onClick={(event) => event.stopPropagation()}
                >
                  {normalizedUrl}
                </a>
              </div>
              <button
                type="button"
                className="metadata-link-tooltip-box__open-link"
                onClick={handleOpenInNewTab}
              >
                <i className="mdi mdi-open-in-new" aria-hidden="true" />
                Open link
              </button>
            </>
          ) : (
            <p className="metadata-link-tooltip-box__empty">No link available.</p>
          )}
        </div>

        <div className="metadata-link-tooltip-box__actions">
          <Button
            size="sm"
            color="light"
            className="metadata-link-tooltip-box__btn metadata-link-tooltip-box__btn--copy"
            onClick={handleCopy}
            disabled={!normalizedUrl || shortening}
          >
            <i className="mdi mdi-content-copy" aria-hidden="true" />
            Copy
          </Button>
          <Button
            size="sm"
            color="primary"
            className="metadata-link-tooltip-box__btn metadata-link-tooltip-box__btn--short"
            onClick={handleShortLink}
            disabled={!normalizedUrl || shortening}
          >
            {shortening ? (
              <Spinner size="sm" />
            ) : (
              <>
                <i className="mdi mdi-link-variant" aria-hidden="true" />
                Short Link
              </>
            )}
          </Button>
        </div>
      </div>
    </Popover>
  );
};

MetadataLinkTooltipBox.propTypes = {
  isOpen: PropTypes.bool,
  toggle: PropTypes.func,
  targetRef: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  url: PropTypes.string,
  title: PropTypes.string,
};

export default MetadataLinkTooltipBox;
